/**
 * Log Viewer Component
 * Displays application logs for debugging
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { clsx } from "clsx";
import {
  Download,
  Trash2,
  RefreshCw,
  Bug,
  AlertTriangle,
  Info,
  XCircle,
  ChevronDown,
  ChevronUp,
  Copy,
} from "lucide-react";
import {
  getLogs,
  getLogsAsText,
  exportLogs,
  clearLogs,
  getStats,
  type LogEntry,
} from "@/services/logger";

const LevelColors: Record<string, string> = {
  ERROR: "text-error-700 bg-error-50 border-error-200",
  WARN: "text-warning-700 bg-warning-50 border-warning-200",
  INFO: "text-info-700 bg-info-50 border-info-200",
  DEBUG: "text-gray-700 bg-gray-50 border-gray-200",
};

const LevelIcons: Record<string, React.ElementType> = {
  ERROR: XCircle,
  WARN: AlertTriangle,
  INFO: Info,
  DEBUG: Bug,
};

interface LogViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LogViewer: React.FC<LogViewerProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState<string>("ALL");
  const [search, setSearch] = useState<string>("");
  const [days, setDays] = useState<number>(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const loadLogs = useCallback(() => {
    const loadedLogs = getLogs(days);
    setLogs(loadedLogs);
    setStats(getStats());
  }, [days]);

  useEffect(() => {
    if (isOpen) {
      loadLogs();
    }
  }, [isOpen, loadLogs]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesLevel = filter === "ALL" || log.level === filter;
      const matchesSearch =
        !search ||
        log.message.toLowerCase().includes(search.toLowerCase()) ||
        log.category.toLowerCase().includes(search.toLowerCase());
      return matchesLevel && matchesSearch;
    });
  }, [logs, filter, search]);

  const handleExport = () => {
    const text = getLogsAsText(days);
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ydm-logs-${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportJSON = () => {
    const json = exportLogs(days);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ydm-logs-${new Date().toISOString().split("T")[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(getLogsAsText(days));
  };

  const handleClear = () => {
    if (confirm("Are you sure you want to clear all logs?")) {
      clearLogs();
      loadLogs();
    }
  };

  const toggleExpanded = (timestamp: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(timestamp)) {
        next.delete(timestamp);
      } else {
        next.add(timestamp);
      }
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold">System Logs</h2>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>{stats.total || 0} total</span>
              <span className="text-error-700">{stats.ERROR || 0} errors</span>
              <span className="text-warning-700">{stats.WARN || 0} warnings</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XCircle size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center space-x-2">
            {/* Filter */}
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink-950"
            >
              <option value="ALL">All Levels</option>
              <option value="ERROR">Errors</option>
              <option value="WARN">Warnings</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>

            {/* Days */}
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink-950"
            >
              <option value={1}>Last 24h</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
            </select>

            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search logs..."
              className="px-3 py-1.5 border rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-ink-950"
            />

            {/* Refresh */}
            <button
              onClick={loadLogs}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopy}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Copy size={16} className="mr-1" />
              Copy
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Download size={16} className="mr-1" />
              Export
            </button>
            <button
              onClick={handleExportJSON}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              JSON
            </button>
            <button
              onClick={handleClear}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-error-700 hover:bg-error-50 rounded-lg transition-colors"
            >
              <Trash2 size={16} className="mr-1" />
              Clear
            </button>
          </div>
        </div>

        {/* Log entries */}
        <div className="flex-1 overflow-auto p-4 bg-gray-900">
          {filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              No logs found
            </div>
          ) : (
            <div className="space-y-1 font-mono text-sm">
              {filteredLogs.map((log, index) => {
                const Icon = LevelIcons[log.level] || Bug;
                const isExpanded = expanded.has(log.timestamp);
                const hasDetails = log.data || log.stack;

                return (
                  <div
                    key={index}
                    className={clsx(
                      "flex items-start space-x-2 p-2 rounded border",
                      LevelColors[log.level]
                    )}
                  >
                    <Icon size={14} className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs">
                          {log.timestamp.split("T")[1]?.split(".")[0]}
                        </span>
                        <span className="text-xs font-medium">
                          [{log.category}]
                        </span>
                        <span className="truncate">{log.message}</span>
                        {hasDetails && (
                          <button
                            onClick={() => toggleExpanded(log.timestamp)}
                            className="flex-shrink-0 hover:opacity-70"
                          >
                            {isExpanded ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="mt-2 pl-2 border-l-2 border-current">
                          {log.data != null && (
                            <pre className="text-xs overflow-auto">
                              {JSON.stringify(log.data as Record<string, unknown>, null, 2)}
                            </pre>
                          )}
                          {log.stack && (
                            <pre className="text-xs overflow-auto whitespace-pre-wrap">
                              {log.stack}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogViewer;
